import React, { useState, useEffect, useCallback, useRef } from 'react';
import HeaderMenu from '../menu/header_container';
import { Row, Col, Form } from 'react-bootstrap';
import { BsSearch } from 'react-icons/bs/index.js';
import config from '../../config';
import DataTable from '../widgets/DataTable';
import './auditoriaIngresos.css';
import { formatDateTime, getPresetDates, PRESETS, renderOrigen, useAvisoInicioAuditoria } from './auditoriaIngresosComun';

const renderEstado = (exitoso) => {
  const isOk = Number(exitoso) === 1;
  return (
    <span className={`ai-badge ${isOk ? 'ai-badge-on' : 'ai-badge-off'}`}>
      <span className="ai-dot" />
      {isOk ? 'Exitoso' : 'Fallido'}
    </span>
  );
};

const renderUsuario = (usuario) => (
  <span style={{ fontWeight: '600', color: '#1b2f55' }}>
    {usuario || '--'}
  </span>
);

const COLUMNS = [
  {
    key: 'FECHA_INGRESO',
    label: 'Fecha y Hora',
    sortable: true,
    render: (val) => formatDateTime(val, '--')
  },
  {
    key: 'ID_EMPRESA',
    label: 'Empresa (NIT)',
    sortable: true,
    render: (val) => <span style={{ fontWeight: '500', color: '#0369a1' }}>{val || '--'}</span>
  },
  {
    key: 'USUARIO',
    label: 'Usuario',
    sortable: true,
    render: (val) => renderUsuario(val)
  },
  {
    key: 'NOMBRE_COMPLETO',
    label: 'Nombre / Razón Social',
    sortable: true,
    render: (val) => val || <span style={{ color: '#94a3b8' }}>No registrado</span>
  },
  {
    key: 'IP_ORIGEN',
    label: 'IP Origen',
    sortable: true,
    render: (val) => <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#334155' }}>{val || '--'}</span>
  },
  {
    key: 'ORIGEN',
    label: 'Canal',
    sortable: true,
    render: (val) => renderOrigen(val)
  },
  {
    key: 'EXITOSO',
    label: 'Estado',
    sortable: true,
    render: (val) => renderEstado(val)
  },
  {
    key: 'DISPOSITIVO',
    label: 'Dispositivo / Navegador',
    render: (val) => <span style={{ fontSize: '12px', color: '#475569' }}>{val || '--'}</span>
  },
  {
    key: 'DETALLE_ESTADO',
    label: 'Detalle',
    render: (val) => <span style={{ fontSize: '12px', color: '#475569' }}>{val || '--'}</span>
  }
];

function AuditoriaIngresosGlobal() {
  useAvisoInicioAuditoria();
  const defaultDates = getPresetDates(30);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePreset, setActivePreset] = useState('mes');
  const [fechaInicio, setFechaInicio] = useState(defaultDates.inicio);
  const [fechaFin, setFechaFin] = useState(defaultDates.fin);
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroOrigen, setFiltroOrigen] = useState('');
  const [filtroExitoso, setFiltroExitoso] = useState('');

  const company = sessionStorage.getItem('company');
  const tipo = sessionStorage.getItem('tipo');

  const lastUrl = useRef('');

  const fetchLogs = useCallback((overrideParams = {}, forzar = false) => {
    const fInicio = overrideParams.fInicio !== undefined ? overrideParams.fInicio : fechaInicio;
    const fFin = overrideParams.fFin !== undefined ? overrideParams.fFin : fechaFin;
    const emp = overrideParams.emp !== undefined ? overrideParams.emp : filtroEmpresa;
    const user = overrideParams.user !== undefined ? overrideParams.user : filtroUsuario;
    const orig = overrideParams.orig !== undefined ? overrideParams.orig : filtroOrigen;
    const exit = overrideParams.exit !== undefined ? overrideParams.exit : filtroExitoso;

    const queryParams = new URLSearchParams();
    if (fInicio) queryParams.append('fecha_inicio', fInicio);
    if (fFin) queryParams.append('fecha_fin', fFin);
    if (emp) queryParams.append('empresa', emp);
    if (user) queryParams.append('usuario', user);
    if (orig) queryParams.append('origen', orig);
    if (exit !== '') queryParams.append('exitoso', exit);

    const url = `${config.servBackEnd}/users/logs/ingresos?${queryParams.toString()}`;

    // Evita repetir la misma consulta (p. ej. preset + efecto de filtros); "Buscar" fuerza la consulta
    if (!forzar && lastUrl.current === url) return;
    lastUrl.current = url;
    setLoading(true);

    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        empresa: company,
        tipo: tipo
      }
    })
      .then((res) => res.json())
      .then((resData) => {
        if (resData.ok && Array.isArray(resData.data)) {
          setData(resData.data);
        } else {
          setData([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error al cargar auditoría global de ingresos:', err);
        setLoading(false);
      });
  }, [company, tipo, fechaInicio, fechaFin, filtroEmpresa, filtroUsuario, filtroOrigen, filtroExitoso]);

  // Espera 400 ms tras el último cambio de filtros (evita una consulta por cada letra); la primera carga es inmediata
  useEffect(() => {
    const t = setTimeout(() => fetchLogs(), lastUrl.current ? 400 : 0);
    return () => clearTimeout(t);
  }, [fetchLogs]);

  const handlePresetSelect = (presetKey, days) => {
    setActivePreset(presetKey);
    const { inicio, fin } = getPresetDates(days);
    setFechaInicio(inicio);
    setFechaFin(fin);
    fetchLogs({ fInicio: inicio, fFin: fin });
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    fetchLogs({}, true);
  };

  const handleLimpiarFiltros = () => {
    setActivePreset('');
    setFechaInicio('');
    setFechaFin('');
    setFiltroEmpresa('');
    setFiltroUsuario('');
    setFiltroOrigen('');
    setFiltroExitoso('');
    fetchLogs({ fInicio: '', fFin: '', emp: '', user: '', orig: '', exit: '' });
  };


  return (
    <React.Fragment>
      <HeaderMenu />
      <div className="ai-root" style={{ padding: '0 0 20px', backgroundColor: '#f8fafc', minHeight: 'calc(100vh - 80px)' }}>
        {/* Banner de Título */}
        <div
          style={{
            background: 'var(--ai-grad)',
            color: 'white',
            padding: '1.4rem 2rem',
            borderRadius: '16px',
            boxShadow: '0 4px 14px rgba(27, 47, 85, 0.25)',
            marginBottom: '10px',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px'
          }}
        >
          <div style={{ textAlign: 'left' }}>
            <h2 style={{ color: 'white', margin: 0, fontWeight: '700', fontSize: '1.5rem', letterSpacing: '-0.4px' }}>
              Auditoría General de Ingresos
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
              Trazabilidad forense cronológica de cada intento de acceso a la plataforma en todas las empresas
            </p>
          </div>
        </div>

      <div className="ai-content">
      {/* Panel de Filtros Superadmin */}
      <div className="dt-wrapper" style={{ overflow: 'visible', padding: '18px 22px', marginBottom: '4px' }}>
        <Form onSubmit={handleSearchSubmit}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
            <h6 style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', margin: 0 }}>
              FILTROS DE BÚSQUEDA GLOBAL
            </h6>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginRight: '4px' }}>Periodo:</span>
              {PRESETS.map((p) => {
                const isActive = activePreset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => handlePresetSelect(p.key, p.days)}
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: isActive ? '700' : '500',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      border: `1px solid ${isActive ? '#1b2f55' : '#cbd5e1'}`,
                      background: isActive ? 'var(--ai-grad)' : '#ffffff',
                      color: isActive ? '#ffffff' : '#334155',
                      cursor: 'pointer',
                      boxShadow: isActive ? '0 2px 6px rgba(27, 47, 85, 0.25)' : 'none'
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Row className="g-3 align-items-end">
            <Col xs={12} sm={6} md>
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Fecha Inicio</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={fechaInicio}
                  onChange={(e) => { setFechaInicio(e.target.value); setActivePreset('custom'); }}
                  style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', height: '34px' }}
                />
              </Form.Group>
            </Col>

            <Col xs={12} sm={6} md>
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Fecha Fin</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={fechaFin}
                  onChange={(e) => { setFechaFin(e.target.value); setActivePreset('custom'); }}
                  style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', height: '34px' }}
                />
              </Form.Group>
            </Col>

            <Col xs={12} sm={6} md>
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Empresa (NIT)</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Todas las empresas"
                  value={filtroEmpresa}
                  onChange={(e) => setFiltroEmpresa(e.target.value)}
                  style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', height: '34px' }}
                />
              </Form.Group>
            </Col>

            <Col xs={12} sm={6} md>
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Usuario</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Todos los usuarios"
                  value={filtroUsuario}
                  onChange={(e) => setFiltroUsuario(e.target.value)}
                  style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', height: '34px' }}
                />
              </Form.Group>
            </Col>

            <Col xs={12} sm={6} md>
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Canal</Form.Label>
                <Form.Control
                  as="select"
                  size="sm"
                  value={filtroOrigen}
                  onChange={(e) => setFiltroOrigen(e.target.value)}
                  style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', height: '34px', backgroundColor: '#fff' }}
                >
                  <option value="">Todos los canales</option>
                  <option value="LOGIN_PRINCIPAL">Plataforma Web</option>
                  <option value="LOGIN_CLIENTES">Portal Clientes</option>
                </Form.Control>
              </Form.Group>
            </Col>

            <Col xs={12} sm={6} md>
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Resultado</Form.Label>
                <Form.Control
                  as="select"
                  size="sm"
                  value={filtroExitoso}
                  onChange={(e) => setFiltroExitoso(e.target.value)}
                  style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', height: '34px', backgroundColor: '#fff' }}
                >
                  <option value="">Todos</option>
                  <option value="1">Exitosos</option>
                  <option value="0">Fallidos</option>
                </Form.Control>
              </Form.Group>
            </Col>

            <Col xs={12} sm={6} md="auto" className="d-flex">
              <button
                type="submit"
                className="dt-btn-create"
                style={{ width: '132px', justifyContent: 'center', height: '34px', padding: '0 8px', fontSize: '0.8rem' }}
              >
                <BsSearch size={12} /> Buscar
              </button>
            </Col>
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
            <button
              type="button"
              onClick={handleLimpiarFiltros}
              style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', padding: '2px 6px' }}
            >
              Limpiar Filtros
            </button>
          </div>
        </Form>
      </div>

      {/* Tabla Forense de Ingresos Globales */}
      <div style={{ marginTop: '-16px' }}>
        <DataTable
          data={data}
          columns={COLUMNS}
          loading={loading}
          searchKeys={['ID_EMPRESA', 'USUARIO', 'NOMBRE_COMPLETO', 'IP_ORIGEN', 'DETALLE_ESTADO', 'DISPOSITIVO']}
          searchPlaceholder="Búsqueda rápida en resultados..."
          emptyText="No se encontraron registros de ingreso con los filtros aplicados"
          defaultItemsPerPage={20}
        />
      </div>
      </div>
      </div>
    </React.Fragment>
  );
}

export default AuditoriaIngresosGlobal;



// WEBPACK FOOTER //
// ./src/components/configuracion/auditoriaIngresosGlobal.jsx